import type { FC } from 'react';

import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import TooltipTrigger from '../../../../components/tooltip_trigger/tooltip_trigger';
import { REVERSE_RELATION_CONFIRM_MODAL } from '../../../../constants/modals';
import { openModal } from '../../../../ducks/modals_manager/slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../store/react_redux_typed';
import Button from '../../../../ui/button/button';

type Props = {
  sourceId: string;
  label: string;
  searchItem: SearchItemEntity;
  onDelete: (
    source: string,
    target: string,
    relation: string,
  ) => Promise<unknown>;
};

const RelationControls: FC<Props> = ({
  label,
  onDelete,
  searchItem,
  sourceId,
}) => {
  const relation = searchItem.relations?.[0];
  const dispatch = useAppDispatch();
  const userRoles = useAppSelector(selectUserRoles);
  if (!relation) {
    return null;
  }

  const displayReverseAction =
    userRoles.can_create_asset_relations &&
    relation.relation_type !== 'SIBLING' &&
    relation.relation_type !== 'DUPLICATE';

  return (
    <>
      {displayReverseAction ? (
        <TooltipTrigger tooltipText="Reverse relation" placement="left">
          <Button
            aria={{ label: 'Reverse relation' }}
            noBorder
            wideBasis={false}
            variant="info"
            iconName="move"
            onClick={() => {
              if (relation.relation_type) {
                const sourceIdValue = relation.related_from_asset_id
                  ? sourceId
                  : searchItem.id;
                const targetId = relation.related_from_asset_id
                  ? searchItem.id
                  : sourceId;
                dispatch(
                  openModal({
                    id: REVERSE_RELATION_CONFIRM_MODAL,
                    contextItems: [
                      {
                        id: searchItem.id,
                        relation: {
                          sourceId: sourceIdValue,
                          targetId,
                          relationType: relation.relation_type,
                          label,
                          originalRelation: relation,
                          assetId: sourceId,
                        },
                        searchItem,
                      },
                    ],
                  }),
                );
              }
            }}
          />
        </TooltipTrigger>
      ) : null}
      {userRoles.can_delete_asset_relations ? (
        <Button
          aria={{ label: 'Delete relation' }}
          variant="danger"
          noBorder
          wideBasis={false}
          iconName="deletion_delete"
          onClick={() => {
            if (relation.relation_type) {
              const source = relation.related_from_asset_id
                ? sourceId
                : searchItem.id;
              const target = relation.related_from_asset_id
                ? searchItem.id
                : sourceId;

              onDelete(source, target, relation.relation_type);
            }
          }}
        />
      ) : null}
    </>
  );
};

export default RelationControls;
